import { useState } from "react";
import { toast } from "react-toastify";
import { useGroupApi } from "../groupApi";
import { GroupAvatar, Sheet, Switch, ConfirmSheet } from "./ui";
import GroupForm, { groupFormData } from "./GroupForm";

// Owner-only. The server re-checks ownership on every call below.
export default function GroupSettings({ group, members, onClose, onUpdated, onMembersChanged, onDeleted }) {
  const { call, json } = useGroupApi();
  const [busy, setBusy] = useState(false);
  const [pendingPerm, setPendingPerm] = useState({});
  const [confirm, setConfirm] = useState(null); // 'delete' | { transfer: member }

  const save = async (values) => {
    setBusy(true);
    try {
      const updated = await call(`groups/${group.group_id}`, { method: "PUT", body: groupFormData(values) });
      toast.success("تغییرات ذخیره شد");
      onUpdated(updated);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const togglePerm = async (m, value) => {
    setPendingPerm((p) => ({ ...p, [m.user_id]: true }));
    try {
      await json(`groups/${group.group_id}/members/${m.user_id}/permissions`, "PATCH", { can_edit_calendar: value });
      onMembersChanged(); // takes effect immediately for that member
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPendingPerm((p) => ({ ...p, [m.user_id]: false }));
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await call(`groups/${group.group_id}`, { method: "DELETE" });
      toast.success("گروه حذف شد");
      onDeleted();
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
      setConfirm(null);
    }
  };

  const doTransfer = async (m) => {
    setBusy(true);
    try {
      await json(`groups/${group.group_id}/transfer-ownership`, "POST", { new_owner_id: m.user_id });
      toast.success(`${m.name} مدیر جدید گروه شد`);
      onClose();
      onUpdated(null);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const others = (members || []).filter((m) => m.role !== "owner");

  if (confirm === "delete") {
    return (
      <ConfirmSheet
        title="حذف گروه"
        message="با حذف گروه، گفتگو و تقویم مشترک برای همه اعضا برای همیشه پاک می‌شه. مطمئنی؟"
        confirmLabel="آره، گروه حذف بشه"
        busy={busy}
        onConfirm={doDelete}
        onClose={() => setConfirm(null)}
      />
    );
  }
  if (confirm && confirm.transfer) {
    return (
      <ConfirmSheet
        title="واگذاری مدیریت"
        message={`مدیریت گروه به ${confirm.transfer.name} داده بشه؟ بعدش تو یه عضو معمولی می‌شی و دیگه نمی‌تونی تنظیمات رو تغییر بدی.`}
        confirmLabel="آره، واگذار کن"
        busy={busy}
        onConfirm={() => doTransfer(confirm.transfer)}
        onClose={() => setConfirm(null)}
      />
    );
  }

  return (
    <Sheet title="تنظیمات گروه" onClose={onClose}>
      <GroupForm initial={group} submitLabel="ذخیره تغییرات" busy={busy} onSubmit={save} />

      <div className="grpSection">
        <div className="grpSectionTitle yekan">دسترسی ویرایش تقویم</div>
        <p className="grey-2 caption-1 yekan" style={{ marginTop: 0 }}>همه اعضا می‌تونن تقویم رو ببینن؛ اینجا تعیین کن کی بتونه تغییرش بده.</p>
        {others.length === 0 && <p className="grey-2 caption-1 yekan">هنوز عضوی به‌جز تو نیست.</p>}
        {others.map((m) => (
          <div className="grpRow" key={m.user_id}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <GroupAvatar name={m.name} size="sm" />
              <span className="yekan">{m.name}</span>
            </div>
            <Switch checked={m.can_edit_calendar} disabled={!!pendingPerm[m.user_id]} label={`ویرایش تقویم ${m.name}`} onChange={(v) => togglePerm(m, v)} />
          </div>
        ))}
      </div>

      {others.length > 0 && (
        <div className="grpSection">
          <div className="grpSectionTitle yekan">واگذاری مدیریت</div>
          <select className="grpInput" defaultValue="" onChange={(e) => {
            const m = others.find((x) => x.user_id === e.target.value);
            if (m) setConfirm({ transfer: m });
            e.target.value = "";
          }}>
            <option value="" disabled>انتخاب عضو...</option>
            {others.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
          </select>
        </div>
      )}

      <div className="grpSection">
        <button type="button" className="grpBtn grpBtn-danger grpBtn-block" onClick={() => setConfirm("delete")}>حذف گروه</button>
      </div>
    </Sheet>
  );
}
